import DateFormat from '@/Enums/Format/DateFormat.enum';
import type { IParsedAdvertiser } from '@/Interfaces/Advertiser.interface';
import MonthYearPicker from '@/components/MonthYearPicker';
import { getAdvertisersFromClients, getAdvertisersFromWhitelabels, getUniqueAdvertisers } from '@/helpers/advertisers';
import { dateRangeToFormat } from '@/helpers/valueFormatter/dateFormatter.helpers';
import AdvertiserSelect from '@/pages/Attribution/Components/Filters/OfflineSales/AdvertiserSelect';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectParsedWithBeeswaxClients,
  selectParsedWithBeeswaxWhitelabels,
} from '@/store/selectors/sharedApi.selectors';
import { setFilters } from '@/store/slices/attribution/offlineSales.slice';
import type { DateRange } from '@mui/x-date-pickers-pro';
import moment, { type Moment } from 'moment';
import { useCallback, useMemo, useState } from 'react';

const Filters = () => {
  const dispatch = useAppDispatch();
  const [dateRange, setDateRange] = useState<DateRange<Moment>>([moment().startOf('month'), moment().endOf('month')]);

  const clients = useAppSelector(selectParsedWithBeeswaxClients);
  const whitelabels = useAppSelector(selectParsedWithBeeswaxWhitelabels);

  const advertiserOptions = useMemo(() => {
    const advertisers = (
      whitelabels?.length ? getAdvertisersFromWhitelabels(whitelabels) : getAdvertisersFromClients(clients)
    ) as IParsedAdvertiser[];

    return getUniqueAdvertisers<IParsedAdvertiser>(advertisers, 'beeswaxId');
  }, [whitelabels, clients]);

  const displayDateRange = useMemo(() => {
    const [startDate, endDate] = dateRange;
    return dateRangeToFormat([startDate!, endDate!], DateFormat.ShortMonthDay);
  }, [dateRange]);

  const dateRangeChange = useCallback(
    (dateRange: DateRange<Moment>) => {
      const [startDate, endDate] = dateRange;

      setDateRange(dateRange);
      dispatch(
        setFilters({
          dateRangeStart: startDate?.format(DateFormat.Iso8601),
          dateRangeEnd: endDate?.format(DateFormat.Iso8601),
        }),
      );
    },
    [dispatch],
  );

  const onAdvertiserChange = useCallback(
    (advertiserId: number) => {
      dispatch(setFilters({ advertiserId }));
    },
    [dispatch],
  );

  return (
    <div className='mb-8'>
      <div className='text-2xl font-semibold'>Offline Sales</div>
      <div className='flex justify-between'>
        <div className='flex items-center'>
          <span className='text-sm text-[#84818A]'>{displayDateRange}</span>
        </div>
        <div className='flex gap-2'>
          <AdvertiserSelect options={advertiserOptions} onChange={onAdvertiserChange} />
          <MonthYearPicker onChange={dateRangeChange} />
        </div>
      </div>
    </div>
  );
};

export default Filters;
